import { useReducer } from 'react'

import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { SlateContextProps } from '@tribeplatform/slate-kit/types'

import { useQuery } from '../../../lib/useQuery'
import { ThemeConvertor } from '../../../themes/theme-convertor'
import { useTheme } from '../../../themes/ThemeProvider.component'
import {
  Action,
  ActionKind,
  ActivateBlockPayload,
  BlockSettingsPayload,
  PreviewState,
  PreviewType,
  SetActiveTabPayload,
  SidebarState,
  SidebarViewType,
  State,
  Update,
} from '../../CanaryPageBuilder/types'

const DEFAULT_UPDATE_STATE = {
  updates: [],
  currentUpdateIdx: -1,
  activeBlock: null,
  selectedBlock: null,
  activeTabs: {},
  blockSettings: [],
}

const init = ({ theme, ...state }: State): State => {
  return {
    theme: new ThemeConvertor({
      base: theme,
      theme: {},
    }).toTheme(),
    ...state,
  }
}

const reducer = (state: State, action: Action): State => {
  const {
    theme,
    updates,
    currentUpdateIdx,
    activeBlock,
    selectedBlock,
    activeTabs,
    blockSettings,
  } = state
  const currentUpdates = updates.slice(0, currentUpdateIdx + 1)

  const lastUpdate = currentUpdateIdx >= 0 ? updates[currentUpdateIdx] : null
  let update: Update

  switch (action.type) {
    case ActionKind.SET_SIDEBAR:
      return {
        ...state,
        sidebar: action?.payload as SidebarState,
      }
    case ActionKind.SET_THEME:
      return {
        ...state,
        theme: new ThemeConvertor({
          base: theme,
          theme: action.payload,
        }).toTheme(),
      }
    case ActionKind.CHANGE_PREVIEW:
      return {
        ...state,
        preview: action.payload as PreviewState,
        ...DEFAULT_UPDATE_STATE,
      }
    case ActionKind.RESET_UPDATES:
      return {
        ...state,
        ...DEFAULT_UPDATE_STATE,
        ...(typeof action.payload === 'object' ? action.payload : {}),
      }
    case ActionKind.NEW_UPDATE: {
      update = action.payload as Update
      const lastKeys = Object.keys(lastUpdate?.slateUpdate || {})
      const currentKeys = Object.keys(update?.slateUpdate || {})
      // let's merge
      if (
        // to make sure our updates only contains updateBlocks
        lastKeys.indexOf('updatedBlocks') === 0 &&
        lastKeys.length === 1 &&
        currentKeys.indexOf('updatedBlocks') === 0 &&
        currentKeys.length === 1 &&
        // if the update blocks are about the same block
        lastUpdate?.type === update.type &&
        lastUpdate.slateUpdate.updatedBlocks &&
        update.slateUpdate.updatedBlocks &&
        lastUpdate.slateUpdate.updatedBlocks[0].id ===
          update.slateUpdate.updatedBlocks[0].id
      ) {
        return {
          ...state,
          updates: [
            ...currentUpdates.slice(0, currentUpdateIdx),
            {
              type: update.type,
              slateUpdate: {
                updatedBlocks: [
                  {
                    ...lastUpdate.slateUpdate.updatedBlocks[0],
                    ...update.slateUpdate.updatedBlocks[0],
                  },
                ],
              },
            },
          ],
        }
      }
      return {
        ...state,
        updates: [...currentUpdates.slice(0, currentUpdateIdx + 1), update],
        currentUpdateIdx: currentUpdateIdx + 1,
      }
    }
    case ActionKind.UNDO_UPDATE:
      return {
        ...state,
        currentUpdateIdx: currentUpdateIdx > 0 ? currentUpdateIdx - 1 : -1,
      }
    case ActionKind.REDO_UPDATE:
      return {
        ...state,
        currentUpdateIdx:
          currentUpdateIdx < updates.length - 1
            ? currentUpdateIdx + 1
            : updates.length - 1,
      }
    case ActionKind.ACTIVATE_BLOCK:
      return {
        ...state,
        activeBlock: (action.payload as ActivateBlockPayload).id,
        blockSettings: [(action.payload as ActivateBlockPayload).settings],
      }
    case ActionKind.PUSH_BLOCK_SETTINGS:
      return {
        ...state,
        blockSettings: [
          ...blockSettings,
          action.payload as BlockSettingsPayload,
        ],
      }
    case ActionKind.POP_BLOCK_SETTINGS:
      return {
        ...state,
        blockSettings:
          blockSettings.length > 0
            ? blockSettings.slice(0, blockSettings.length - 1)
            : [],
        activeBlock: blockSettings.length === 1 ? null : activeBlock,
      }
    case ActionKind.DEACTIVATE_BLOCK:
      return {
        ...state,
        activeBlock:
          activeBlock === (action.payload as string) ? null : activeBlock,
      }
    case ActionKind.SELECT_BLOCK:
      return { ...state, selectedBlock: action.payload as string }
    case ActionKind.UNSELECT_BLOCK:
      return {
        ...state,
        selectedBlock:
          selectedBlock === (action.payload as string) ? null : selectedBlock,
      }
    case ActionKind.SET_ACTIVE_TAB:
      return {
        ...state,
        activeTabs: {
          ...activeTabs,
          [(action.payload as SetActiveTabPayload).id]: (
            action.payload as SetActiveTabPayload
          ).tab,
        },
      }
    case ActionKind.SET_CONTEXT:
      return { ...state, context: action.payload as SlateContextProps }
    default:
      return state
  }
}
const INITIAL_STATE: Partial<State> = {
  preview: {
    type: PreviewType.THEME,
    id: null,
  },
}

export const useCustomizer = () => {
  const { data: authToken } = useAuthToken()
  const { theme: mainTheme } = useTheme()
  const query = useQuery()

  const context: SlateContextProps = {
    blocksOutput: {},
    network: authToken?.network,
    actorId: authToken?.member?.id,
    space: null,
    networkId: authToken?.network?.id,
    spaceId: null,
    path: '/',
    urlParams: {},
    queryParams: {},
    activeTabs: DEFAULT_UPDATE_STATE.activeTabs,
    inputs: {},
    mode: 'edit',
  }

  const [state, dispatch] = useReducer(
    reducer,
    {
      theme: mainTheme,
      sidebar: {
        type: SidebarViewType.DEFAULT,
      },
      preview: {
        type: (query.type as PreviewType) || INITIAL_STATE.preview.type,
        id: query.id || INITIAL_STATE.preview.id,
      },
      context,
      ...DEFAULT_UPDATE_STATE,
    },
    init,
  )

  return {
    state,
    dispatch,
  }
}
